import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { defaultElectionBasis, fetchRegions, fetchCoverage } from 'src/api/national';

/**
 * Scope + election-context state for the /national section.
 *
 * Scope is a single {level, geoKey} pair rather than separate region/state/
 * district fields, because that is exactly what every endpoint takes. Keeping
 * the shape identical on both sides removes a translation step that would
 * otherwise be a place for the two to disagree.
 *
 * Coverage is loaded once here and shared. Every screen needs it to answer
 * "is this state loaded at all", and a state that is absent must render
 * differently from a state with zero voters.
 *
 * Regions are loaded here for the same reason: both the region picker and the
 * unit table's Region column need state-to-region membership, and it never
 * changes within a session.
 */

const NationalContext = createContext(null);

export const useNational = () => {
  const context = useContext(NationalContext);
  if (!context) throw new Error('useNational must be used inside NationalProvider');
  return context;
};

export const NATION_SCOPE = { level: 'nation', geoKey: 'US' };

export function NationalProvider({ children }) {
  const [scope, setScope] = useState(NATION_SCOPE);
  /**
   * Election basis — GLOBAL, so both tabs agree on what "likely voter" means.
   *
   * Two panels on different bases disagree silently: the same district would
   * show 424,812 likely on one tab and a different number on the other, both
   * plausible. Holding it here is what makes that impossible.
   *
   * Seeded from the current year (2026 -> midterm) rather than fixed at
   * general, which is what every call hardcoded until 2026-08-03 — the midterm
   * rows had been built and never once read.
   */
  const [basis, setBasis] = useState(defaultElectionBasis);
  const [coverage, setCoverage] = useState(null);
  const [coverageError, setCoverageError] = useState(null);
  const [loadingCoverage, setLoadingCoverage] = useState(true);
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoadingCoverage(true);
    fetchCoverage()
      .then((data) => {
        if (!cancelled) setCoverage(data);
      })
      .catch((error) => {
        if (!cancelled) setCoverageError(error?.message || 'Could not load coverage');
      })
      .finally(() => {
        if (!cancelled) setLoadingCoverage(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Non-critical: the pickers degrade to "all states" without it rather than
    // failing the page, so this gets no error state of its own.
    fetchRegions()
      .then((data) => {
        if (!cancelled) setRegions(data?.regions ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Which region a state belongs to. Returns undefined for an unknown state.
   *
   * ⚠️ Never infer this from the geo_key: region codes and state codes share a
   * namespace (region NE is the Northeast, state NE is Nebraska), so membership
   * has to be looked up, not pattern-matched.
   */
  const regionOf = useCallback(
    (stateCode) => regions.find((r) => r.states.includes(stateCode)),
    [regions]
  );

  /** Look up a state's coverage row. Undefined means NOT LOADED, not empty. */
  const coverageFor = useCallback(
    (stateCode) => coverage?.states?.find((s) => s.state === stateCode),
    [coverage]
  );

  const drillTo = useCallback((level, geoKey) => setScope({ level, geoKey }), []);
  const resetScope = useCallback(() => setScope(NATION_SCOPE), []);

  const value = useMemo(
    () => ({
      scope,
      setScope,
      drillTo,
      resetScope,
      basis,
      setBasis,
      coverage,
      coverageFor,
      coverageError,
      loadingCoverage,
      regions,
      regionOf,
      /** State code for the current scope, or null at nation/region level. */
      currentState:
        scope.level === 'state'
          ? scope.geoKey
          : scope.level === 'congressional'
            ? scope.geoKey.split('-')[0]
            : null,
    }),
    [scope, drillTo, resetScope, basis, coverage, coverageFor, coverageError, loadingCoverage, regions, regionOf]
  );

  return <NationalContext.Provider value={value}>{children}</NationalContext.Provider>;
}

NationalProvider.propTypes = {
  children: PropTypes.node,
};
